/**
 * Pure path-setter for the Human Call tab's shared edit handler.
 *
 * Output keys come in two flavours, matching the concrete keys produced by
 * {@link buildScriptModel} / `parseArrayItemPath`:
 *   - top-level scalar id            → "insurance_plan_name"
 *   - indexed entity-relative path   → "procedure[1].coverage.coverage_status"
 *
 * `setOutputAtPath` returns a SHALLOW-cloned output object with `value` written
 * at `key`, creating intermediate objects/arrays as needed. The input object and
 * every container *not* on the write path are left untouched (structural sharing),
 * so React/`useDispositionHelper` see a new top-level reference without us
 * mutating prior state. Sibling array entries (e.g. `procedure[0]`) are preserved.
 */

/** One parsed key segment: a plain object key, or an array key + index. */
interface Segment {
  /** The property name on the parent container (the array name, for array segments). */
  name: string;
  /** Array element index when this segment indexes into an array, else null. */
  index: number | null;
}

/**
 * Split a key like `procedure[1].coverage.coverage_status` into ordered segments:
 *   [{name:'procedure',index:1}, {name:'coverage',index:null}, {name:'coverage_status',index:null}]
 * A plain `insurance_plan_name` yields a single `{name, index:null}` segment.
 */
function parseSegments(key: string): Segment[] {
  const segments: Segment[] = [];
  for (const raw of key.split('.')) {
    const match = /^(.+?)\[(\d+)\]$/.exec(raw);
    if (match) {
      segments.push({ name: match[1], index: Number(match[2]) });
    } else {
      segments.push({ name: raw, index: null });
    }
  }
  return segments;
}

/**
 * Shallow-clone a container we are about to descend THROUGH.
 *
 * In this key grammar that container is always an object, whatever the next segment
 * looks like: `name[i]` means "property `name` of the current object is the array", so
 * even an indexed next segment reads a NAMED property off it. Arrays are created only by
 * the index branch of `setOutputAtPath`, for the segment that carries the index.
 *
 * Choosing the kind from the next segment (the old `nextIsArray`) turned the object
 * above an indexed leaf into `[]` — `drug[0].prior_auth_process.methods[1]` replaced
 * `prior_auth_process` with an empty array, then hung `methods` on it as a named key
 * that JSON drops, wiping the whole section on save (LA-7951). A non-object here — a
 * section already wiped to `[]` by that bug — becomes `{}`, so writing into it repairs
 * the shape instead of silently vanishing.
 */
function cloneObjectContainer(value: unknown): Record<string, unknown> {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return { ...(value as Record<string, unknown>) };
  }
  return {};
}

/**
 * The top-level output field a key writes into: `drug[0].coverage.copay` → `drug`,
 * `insurance_plan_name` → `insurance_plan_name`.
 */
export function outputPathRootKey(key: string): string {
  return parseSegments(key)[0]?.name ?? key;
}

/**
 * Returns a shallow-cloned `output` with `value` set at `key`.
 *
 * @param output the current output object (never mutated)
 * @param key    a top-level id or an `entity[i].relative.path` key
 * @param value  the value to write at `key`
 */
export function setOutputAtPath(
  output: Record<string, unknown>,
  key: string,
  value: unknown,
): Record<string, unknown> {
  const segments = parseSegments(key);
  if (segments.length === 0) return { ...output };

  const root: Record<string, unknown> = { ...(output ?? {}) };
  // `parent` is the container we will write the next segment into.
  let parent: unknown[] | Record<string, unknown> = root;

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    const isLast = i === segments.length - 1;

    // Read the existing value at this segment's property name.
    const existing = (parent as Record<string, unknown>)[seg.name];

    if (seg.index === null) {
      // Plain object key.
      if (isLast) {
        (parent as Record<string, unknown>)[seg.name] = value;
      } else {
        const cloned = cloneObjectContainer(existing);
        (parent as Record<string, unknown>)[seg.name] = cloned;
        parent = cloned;
      }
    } else {
      // Array key: ensure an array container exists, then descend into the index.
      const arr = Array.isArray(existing) ? [...existing] : [];
      (parent as Record<string, unknown>)[seg.name] = arr;
      // Pad any gap below the target index with empty objects. Without this, a
      // write to `entity[i]` on a shorter array leaves sparse holes that
      // `JSON.stringify` serializes as `null` — and the whole-array $set would
      // then persist those nulls, clobbering sibling instances disposition reads.
      // Existing entries (from the spread above) are preserved; only genuinely
      // absent gap slots become `{}`. Entity arrays hold objects, so `{}` is a
      // benign, serializable placeholder.
      for (let pad = arr.length; pad < seg.index; pad++) {
        arr[pad] = {};
      }
      if (isLast) {
        arr[seg.index] = value;
      } else {
        const cloned = cloneObjectContainer(arr[seg.index]);
        arr[seg.index] = cloned;
        parent = cloned;
      }
    }
  }

  return root;
}
